import {
  resolveOwnerMember,
  resolveOwnerName,
  type OwnerMemberLike,
  type SessionOwnerIdentity,
} from "./session-scope.js";

// What a FAILED session-transcript read actually means, and what to offer the person
// looking at it. Pure + dependency-free so the decision is unit-testable without a DOM —
// the branch that had the bug is a decision, not a pixel.
//
// THE BUG THIS DELETES: `fetch_agent_timeline` / `fetch_session_context` are gated by the
// relay's DAEMON_SESSION_READ_TYPES rule — readable ONLY by the person who spoke (no owner,
// admin or channel-member arm). Anyone else therefore gets a hard, PERMANENT rejection. ReadOnlySessionViewer collapsed every failure into one
// boolean, so it answered that wall with "Couldn't load this session's history — the
// request failed" plus THREE controls that re-issue the identical denied RPC (the
// transcript's "Try again", the banner's "Retry context", the footer's "Refresh").
// Generic reason, and not one click that could ever succeed.
//
// THE SOURCE ALREADY DISTINGUISHES THEM, and nothing had to be added there: the relay's
// respondError runs classifyDenialReason over its own denial prose and puts a stable
// `code` on the cyborg:error envelope ("Not authorized to read this session" →
// `not_authorized`), and core/client.ts's buildRpcError already threads that code onto
// the rejected Error as `.code`. This reads the code that was on the wire all along.
// The set is deliberately narrow. It holds ONLY the codes this specific gate can answer
// with, so a broader relay denial family (rate_limited, license_required, …) — and every
// transient failure (a dropped socket, "Client disconnected", withTimeout's "Timed out
// after …ms", which carry no code at all) — stays on the retryable path. Fail-OPEN on
// purpose, the opposite of a server authz check: mis-reading a denial as transient costs
// one wasted Retry, while mis-reading a timeout as a permission wall would tell a
// legitimate owner they are locked out of their own session.
const DENIED_CODES: ReadonlySet<string> = new Set([
  // relay-standalone.ts classifyDenialReason: /not authorized/i — the session-read gate's
  // own two branches ("Not authorized to read this session", cross-tenant + ownership).
  "not_authorized",
  // A call site that supplies the stable code itself rather than prose.
  "forbidden",
  // /not a (workspace )?member/i — the caller is not in the agent's real workspace.
  "not_a_member",
  // /(is not part of|does not belong to) this workspace/i.
  "cross_workspace",
]);

// The ownerless sentence, exported because a second surface now says it: the agent-DM
// timeline, which has no owner/member context to resolve a name from. One string, so the
// wall and the notice cannot drift into two different accounts of the same refusal.
export const SESSION_READ_DENIED_REASON =
  "This session is private. Only the person who started it can open its transcript — nothing here failed, it just isn't yours.";

/** Was this rejection the permission gate, rather than a transient failure? */
export function isSessionReadDenied(err: unknown): boolean {
  const code = (err as { code?: unknown } | null | undefined)?.code;
  return typeof code === "string" && DENIED_CODES.has(code);
}

// The per-daemon scope gate answers with one MORE code than the session-read gate can:
// `no_daemon_access` (relay classifyDenialReason's /access to (this|any) (daemon|agent)/i
// — "no terminal access to this daemon"). It is deliberately NOT in DENIED_CODES above:
// classifySessionReadFailure's copy says "this session is private", which is
// the wrong sentence for a machine you have no access to.
const DAEMON_READ_DENIED_CODES: ReadonlySet<string> = new Set([
  ...DENIED_CODES,
  "no_daemon_access",
]);

/**
 * Was this DAEMON-FORWARDED read (a browser/terminal directory pull, anything the relay
 * routes to a machine) refused by an authorization gate, rather than failing?
 *
 * MEASURED: one user's client sent `cyborg:list_browser_sessions` 12 times in 30 minutes
 * and was refused every time, because the two callers swallowed the rejection into a
 * `console.debug` and their `$effect` re-ran on every unrelated daemon push. Retrying
 * cannot grant access, so a refusal has to be recorded and obeyed.
 *
 * Fail-OPEN like its sibling: a timeout or a dropped socket carries no code and stays on
 * the retryable path.
 */
export function isDaemonReadDenied(err: unknown): boolean {
  const code = (err as { code?: unknown } | null | undefined)?.code;
  return typeof code === "string" && DAEMON_READ_DENIED_CODES.has(code);
}

// ONE value with two shapes, deliberately NOT two booleans: splitting a load-state flag
// in two is how a reader ends up gated on the dead half and renders nothing.
//   "transient" — the ONLY kind a Retry can fix; keeps the existing retry surface.
//   "denied"    — a permanent wall: a real reason, and no Retry at all.
export type SessionReadFailure =
  | { kind: "transient" }
  | {
      kind: "denied";
      /** One plain sentence a person recognises. Never a raw id, never an error code. */
      reason: string;
    };

const TRANSIENT: SessionReadFailure = { kind: "transient" };

/**
 * Decide what a failed session read should say. A denial offers no action. With the owner
 * context (any session but a regular channel's) it reads as today, naming the starter; without
 * it (a regular-channel session, CYBO-1846 Option A) it names nobody.
 */
export function classifySessionReadFailure(
  err: unknown,
  today?: { owner: SessionOwnerIdentity; members: readonly OwnerMemberLike[] },
): SessionReadFailure {
  if (!isSessionReadDenied(err)) return TRANSIENT;
  const name = today && resolveOwnerMember(today.owner, today.members) ? resolveOwnerName(today.owner, today.members) : null; // prettier-ignore
  const reason = name
    ? `This session is private to ${name}. Only they can open its transcript — nothing here failed, it just isn't yours.`
    : SESSION_READ_DENIED_REASON;
  return { kind: "denied", reason };
}

// The work view's empty body (CYBO-1846 Option A), or undefined to keep its own copy. Only the
// server's refusal decides: a non-starter's empty page is an empty session (the view mounts
// only for a session the server listed for them), never "private".
export function sessionWorkDeniedReason(input: {
  /** The decided session-page fallback reason (session-work-mode.ts); undefined = deciding. */
  fallback: string | undefined;
  /** The session-page read itself was refused and latched. */
  refused: boolean;
  loadError: boolean;
  /** The viewer's email matches the session's initiator: never denied (fail-open). */
  viewerStarted: boolean;
}): string | undefined {
  if (input.viewerStarted || input.fallback === undefined) return undefined;
  return input.refused || input.fallback === "denied" ? SESSION_READ_DENIED_REASON : undefined;
}
